import turfDistance from '@turf/distance';
import { decodeSegmentLatLng, getLineColor, getLines, getSegments } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import { decodeSegmentIdxElevHash, demPtToNdim } from '../at_map_helpers';
import { MapStatsUtil } from '../map_stats_util';

const getIncrStats = (currNdim, prevNdim, displayMetric) => {
  const currLatLng = [currNdim[0], currNdim[1]];
  const convertedElev = MapStatsUtil.elevationConversion(currNdim[2], displayMetric);
  if (!prevNdim) {
    return {
      distFromLast: 0,
      elevGainFromLast: 0,
      convertedElev,
      currLatLng
    };
  }

  // Calculate distance from last point
  const currLngLat = currLatLng.slice().reverse();
  const prevLngLat = [prevNdim[1], prevNdim[0]];
  const incrDistanceInKm = turfDistance(prevLngLat, currLngLat);
  const distFromLast = displayMetric ? Math.round(incrDistanceInKm * 10 ** 5) / 10 ** 5 : MapStatsUtil.metersToMiles(incrDistanceInKm * 1000, 5);

  // Calculate elevation gain form last point
  const currElev = currNdim[2];
  const prevElev = prevNdim[2];
  let elevGainFromLast = 0;
  if (currElev > prevElev) {
    elevGainFromLast = currElev - prevElev;
  }

  return {
    distFromLast,
    elevGainFromLast,
    convertedElev,
    currLatLng
  };
};

// Group by segment for recordings, group by route for maps
// Grouping behavior defines how gaps are shown on elevation graph
const getGroupedSegments = atMap => {
  if (!atMap) {
    return [];
  }
  const isRecording = atMap.presentationType === 'track';
  const groupedSegments = [];
  getLines(atMap).forEach(atLine => {
    const color = getLineColor(atLine);
    const lineSegments = getSegments(atLine).map(segment => {
      if (isRecording) {
        groupedSegments.push({
          segments: [segment],
          color
        });
      }
      return segment;
    });
    if (!isRecording && lineSegments.length > 0) {
      groupedSegments.push({
        segments: lineSegments,
        color
      });
    }
  });
  return groupedSegments;
};

const processGroupedSegments = (groupedSegments, displayMetric) => {
  const pointByPointSegmented = [];
  let totalDistance = 0;
  let elevationGain = 0;
  groupedSegments.forEach(group => {
    const pointByPoint = [];
    group.segments.forEach(segment => {
      const segPoints = decodeSegmentLatLng(segment);
      const segIdxElevs = decodeSegmentIdxElevHash(segment);
      segPoints.forEach((curr, i) => {
        const currNdim = [curr[0], curr[1], segIdxElevs[i] || 0];
        let prevNdim;
        if (i > 0) {
          const prev = segPoints[i - 1];
          prevNdim = [prev[0], prev[1], segIdxElevs[i - 1] || 0];
        }
        const { distFromLast, elevGainFromLast, convertedElev, currLatLng } = getIncrStats(currNdim, prevNdim, displayMetric);
        const pointData = [totalDistance, convertedElev, currLatLng];
        // Push totalDistance, converted elevation and lat/lng onto pointByPoint
        pointByPoint.push(pointData);
        // Increment distance and elevation gain counters
        totalDistance += distFromLast;
        elevationGain += elevGainFromLast;
      });
    });
    pointByPointSegmented.push(pointByPoint);
  });
  return {
    pointByPointSegmented,
    totalDistance,
    elevationGain
  };
};

const processElevations = (results, displayMetric) => {
  const elevations = results.elevations;
  const pointByPointSegmented = [];
  const pointByPoint = [];
  let distToPoint = 0;
  elevations.forEach((curr, i) => {
    const currNdim = demPtToNdim(curr);
    let prevNdim;
    if (i > 0) {
      prevNdim = demPtToNdim(elevations[i - 1]);
    }
    const { distFromLast, elevGainFromLast, convertedElev, currLatLng } = getIncrStats(currNdim, prevNdim, displayMetric);
    const pointData = [distToPoint, convertedElev, currLatLng];
    // Push distToPoint, converted elevation and lat/lng onto pointByPoint
    pointByPoint.push(pointData);
    // Increment distance counters
    distToPoint += distFromLast;
  });
  if (pointByPoint.length > 0) {
    pointByPointSegmented.push(pointByPoint);
  }
  // Return server-calculated stats
  const serverCalculatedDistance = displayMetric ? results.stats.distance / 1000.0 : MapStatsUtil.metersToMiles(results.stats.distance);
  return {
    pointByPointSegmented,
    totalDistance: serverCalculatedDistance,
    elevationGain: results.stats.gain
  };
};

export { processElevations, processGroupedSegments, getGroupedSegments };
